import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function DeployGuide() {
  const [copiedStep, setCopiedStep] = useState<string | null>(null);

  const copyToClipboard = (text: string, stepId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(stepId);
    setTimeout(() => setCopiedStep(null), 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              DEPLOYMENT GUIDE
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Deploy to <span className="text-teal-500">Netlify</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get your TeamPro Management platform live in just 5 minutes. No Git, no command line expertise required!
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">5 min</div>
              <div className="text-gray-700 font-medium">Total Time</div>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">3 Steps</div>
              <div className="text-gray-700 font-medium">Simple Process</div>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">$0</div>
              <div className="text-gray-700 font-medium">Completely Free</div>
            </div>
          </div>

          {/* What You Need */}
          <div className="bg-gray-50 rounded-2xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-checkbox-circle-line text-teal-500 mr-3"></i>
              What You Need
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <i className="ri-check-line text-teal-500 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900">Your Project Files</div>
                  <div className="text-sm text-gray-600">You already have these!</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-check-line text-teal-500 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900">A Web Browser</div>
                  <div className="text-sm text-gray-600">Chrome, Firefox, Safari, etc.</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-check-line text-teal-500 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900">5 Minutes</div>
                  <div className="text-sm text-gray-600">That's all it takes!</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-close-line text-red-500 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900">No Git Required</div>
                  <div className="text-sm text-gray-600">Zero technical skills needed</div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 1: Build */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 mb-8">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-12 h-12 bg-teal-500 text-white rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Build Your Project</h3>
                <p className="text-gray-600">Takes about 2 minutes</p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Open Terminal/Command Prompt:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start space-x-2">
                    <i className="ri-windows-line text-teal-500 mt-1"></i>
                    <span><strong>Windows:</strong> Press Win + R, type "cmd", press Enter</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <i className="ri-apple-line text-teal-500 mt-1"></i>
                    <span><strong>Mac:</strong> Press Cmd + Space, type "Terminal", press Enter</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <i className="ri-ubuntu-line text-teal-500 mt-1"></i>
                    <span><strong>Linux:</strong> Press Ctrl + Alt + T</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Navigate to Your Project:</h4>
                <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm relative">
                  <code>cd path/to/your/teampro-management-project</code>
                  <button
                    onClick={() => copyToClipboard('cd path/to/your/teampro-management-project', 'step1-nav')}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-xs transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {copiedStep === 'step1-nav' ? (
                      <><i className="ri-check-line"></i> Copied!</>
                    ) : (
                      <><i className="ri-file-copy-line"></i> Copy</>
                    )}
                  </button>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Build the Project:</h4>
                <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm relative">
                  <code>npm run build</code>
                  <button
                    onClick={() => copyToClipboard('npm run build', 'step1-build')}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-xs transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {copiedStep === 'step1-build' ? (
                      <><i className="ri-check-line"></i> Copied!</>
                    ) : (
                      <><i className="ri-file-copy-line"></i> Copy</>
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Wait 30-60 seconds. A "dist" folder will be created in your project.
                </p>
              </div>
            </div>
          </div>

          {/* Step 2: Deploy */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 mb-8">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-12 h-12 bg-teal-500 text-white rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Deploy to Netlify Drop</h3>
                <p className="text-gray-600">Takes about 1 minute</p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Go to Netlify Drop:</h4>
                <a
                  href="https://app.netlify.com/drop"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line"></i>
                  <span>Open Netlify Drop</span>
                </a>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Find Your "dist" Folder:</h4>
                <p className="text-gray-700 mb-2">In your project folder, locate the "dist" folder that was created in Step 1.</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Drag and Drop:</h4>
                <div className="bg-teal-50 border-2 border-dashed border-teal-300 rounded-lg p-8 text-center">
                  <i className="ri-upload-cloud-2-line text-6xl text-teal-500 mb-4"></i>
                  <p className="text-gray-700 font-medium mb-2">Drag the entire "dist" folder here</p>
                  <p className="text-sm text-gray-600">Upload takes 10-20 seconds</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Get Your Live URL:</h4>
                <p className="text-gray-700">
                  Netlify will show you a URL like: <code className="bg-gray-100 px-2 py-1 rounded text-sm">random-name-123456.netlify.app</code>
                </p>
                <p className="text-sm text-teal-600 font-semibold mt-2">Save this URL!</p>
              </div>
            </div>
          </div>

          {/* Step 3: Environment Variables */}
          <div className="bg-white border-2 border-red-200 rounded-2xl p-8 mb-8">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Add Environment Variables</h3>
                <p className="text-red-600 font-semibold">⚠️ CRITICAL: Your site won't work without these!</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-yellow-800 font-medium">
                  <i className="ri-alert-line mr-2"></i>
                  You must create a Netlify account to add environment variables
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Access Site Settings:</h4>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>Go to your Netlify dashboard</li>
                  <li>Click on your site</li>
                  <li>Go to "Site settings"</li>
                  <li>Click "Environment variables" in sidebar</li>
                  <li>Click "Add a variable"</li>
                </ol>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Add Supabase URL:</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-gray-700">Key:</span>
                    <code className="bg-white px-3 py-1 rounded border border-gray-200">VITE_PUBLIC_SUPABASE_URL</code>
                  </div>
                  <div className="text-sm text-gray-600">
                    <i className="ri-information-line text-teal-500 mr-1"></i>
                    Copy the value from your .env file
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Add Supabase Anon Key:</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-gray-700">Key:</span>
                    <code className="bg-white px-3 py-1 rounded border border-gray-200">VITE_PUBLIC_SUPABASE_ANON_KEY</code>
                  </div>
                  <div className="text-sm text-gray-600">
                    <i className="ri-information-line text-teal-500 mr-1"></i>
                    Copy the value from your .env file
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Redeploy:</h4>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>Go to "Deploys" tab</li>
                  <li>Click "Trigger deploy" → "Deploy site"</li>
                  <li>Wait 30 seconds</li>
                  <li className="text-teal-600 font-semibold">Your site is now fully functional! 🎉</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Testing Checklist */}
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-task-line text-teal-500 mr-3"></i>
              Test Your Live Site
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Homepage Tests:</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4 text-teal-500 cursor-pointer" />
                    <span>Page loads without errors</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4 text-teal-500 cursor-pointer" />
                    <span>Images display correctly</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4 text-teal-500 cursor-pointer" />
                    <span>Navigation menu works</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Equipment Store:</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4 text-teal-500 cursor-pointer" />
                    <span>Products display correctly</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4 text-teal-500 cursor-pointer" />
                    <span>Filters work properly</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4 text-teal-500 cursor-pointer" />
                    <span>"Order Now" buttons work</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Payment Flow:</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4 text-teal-500 cursor-pointer" />
                    <span>Stripe checkout opens</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4 text-teal-500 cursor-pointer" />
                    <span>Test card works (4242...)</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4 text-teal-500 cursor-pointer" />
                    <span>Order completes successfully</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Mobile Tests:</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4 text-teal-500 cursor-pointer" />
                    <span>Site works on mobile</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4 text-teal-500 cursor-pointer" />
                    <span>Navigation responsive</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4 text-teal-500 cursor-pointer" />
                    <span>Buttons are clickable</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Troubleshooting */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-tools-line text-teal-500 mr-3"></i>
              Troubleshooting
            </h2>
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Issue: Site Shows "Page Not Found"</h4>
                <p className="text-gray-700 mb-2"><strong>Solution:</strong></p>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Make sure you uploaded the "dist" folder, not the entire project</li>
                  <li>The "dist" folder should contain "index.html" and "assets" folder</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Issue: Products Don't Load / Blank Pages</h4>
                <p className="text-gray-700 mb-2"><strong>Solution:</strong></p>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Environment variables not added</li>
                  <li>Go to Site Settings → Environment Variables</li>
                  <li>Add both Supabase variables</li>
                  <li>Redeploy the site</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Issue: "Order Now" Buttons Don't Work</h4>
                <p className="text-gray-700 mb-2"><strong>Solution:</strong></p>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Check browser console (Press F12)</li>
                  <li>Verify Supabase environment variables are correct</li>
                  <li>Check Stripe is connected in Supabase</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Success Banner */}
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl p-12 text-center">
            <i className="ri-rocket-line text-6xl text-white mb-4"></i>
            <h2 className="text-3xl font-bold text-white mb-4">Congratulations! You're Live!</h2>
            <p className="text-teal-50 text-lg mb-8 max-w-2xl mx-auto">
              Your TeamPro Management platform is now accessible worldwide, secure with HTTPS, and ready for customers!
            </p>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 bg-white text-teal-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              <span>Back to Homepage</span>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
